import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { createHubConnection } from '../api/hub';
import type { ConnectionStatus, MessagePostedNudge } from '../api/hub';

/**
 * The app's one connection to the hub.
 *
 * **One socket per client, not one per conversation**, and the reasoning is worth keeping where
 * somebody will read it:
 *
 * - A socket costs a TCP and TLS handshake and server memory. N of them for N open threads is
 *   waste, and browsers cap concurrent connections per origin.
 * - A badge on the chat icon has to light up for threads nobody has open. A per-conversation
 *   subscription structurally cannot deliver that — it can only ever tell you about the thread you
 *   already asked about, which is exactly why the conversations list did not refresh.
 * - A phone forces it anyway. A native client holds one socket while foregrounded and none while
 *   backgrounded, where delivery is APNs or FCM addressed to a *user*. Keying the fan-out on people
 *   rather than conversations means the socket path and the push path are one decision, made once,
 *   on the server, at the moment a message is posted.
 *
 * So the client subscribes to nothing. It connects, and the server sends it what its owner may
 * read. Nothing here is durable: the payload carries a cursor and never content, and every screen
 * reaches the same state through `?after=` if this connection never establishes at all.
 */
interface LiveMessages {
  status: ConnectionStatus;
  /** Every nudge this client has been sent, newest last. Screens read the tail they care about. */
  latest: MessagePostedNudge | null;
  /** Bumped on every nudge, so a screen can depend on "something happened" without diffing. */
  tick: number;
  connection: HubConnection | null;
}

const Context = createContext<LiveMessages>({
  status: 'disconnected',
  latest: null,
  tick: 0,
  connection: null,
});

export function LiveMessagesProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [latest, setLatest] = useState<MessagePostedNudge | null>(null);
  const [tick, setTick] = useState(0);
  const connectionRef = useRef<HubConnection | null>(null);
  const [connection, setConnection] = useState<HubConnection | null>(null);

  useEffect(() => {
    const hub = createHubConnection();
    connectionRef.current = hub;

    hub.on('messagePosted', (nudge: MessagePostedNudge) => {
      setLatest(nudge);
      setTick((n) => n + 1);
    });

    hub.onreconnecting(() => setStatus('reconnecting'));
    hub.onreconnected(() => {
      setStatus('connected');
      // A reconnect is a real, load-bearing path -- every deploy drops every connection. Screens
      // reconcile by cursor off this, rather than anything trying to replay what was missed.
      setTick((n) => n + 1);
    });
    hub.onclose(() => setStatus('disconnected'));

    hub
      .start()
      .then(() => {
        setStatus('connected');
        setConnection(hub);
      })
      // Silent: a client that cannot connect is a client without live updates, not a broken one.
      .catch(() => setStatus('disconnected'));

    return () => {
      connectionRef.current = null;
      void hub.stop();
    };
  }, []);

  const value = useMemo(
    () => ({ status, latest, tick, connection }),
    [status, latest, tick, connection],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useLiveMessages() {
  return useContext(Context);
}
